import React, { Fragment } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Tooltip } from '@mui/material';
import { ProposalProduct } from '@trnsact/trnsact-shared-types/src/generated';
import { useMenuOptionTerms } from '../../../hooks';
import { TermsStepperManager } from '../TermsStepperManager';
import { deskingActions, deskingSelectors } from '../../../model';
import { ProposalProductsCard, ProposalProductsCards } from '../index';
import { ProductConfig, ProposalProductCardModes } from '../../../types';
import { hideFieldsForRender, prisingConfigByVendor } from '../../../constants';
import { getProductFactHashMapKey } from 'modules/desking/lib';

export const MenuCards = () => {
  const dispatch = useDispatch();

  const { currentTerm, ...termsManagementProps } = useMenuOptionTerms();

  const classes = useStyles();

  const builder = useSelector(deskingSelectors.builder);
  const products = useSelector(deskingSelectors.proposalsProducts);
  const currentEquipment = useSelector(deskingSelectors.currentEquipment);
  const selectedMenuOption = useSelector(deskingSelectors.selectedMenuOption);
  const isNeedRunJsonEngine = useSelector(deskingSelectors.isNeedRunJsonEngine);
  const selectedProducts = useSelector(deskingSelectors.selectedProposalsProductsArray);
  const menuOptionsGroupedByName = useSelector(deskingSelectors.menuOptionsGroupedByName);
  const productConfigurations = useSelector(deskingSelectors.proposalProductConfigurations);
  const productConfigurationsInMenu = useSelector(deskingSelectors.proposalProductConfigurationsInMenu);
  const selectedProductsConfigurations = useSelector(deskingSelectors.selectedProductsConfigurations);
  const selectedProductsFactsForCheck = useSelector(deskingSelectors.selectedProductsFactsForCheck);
  const selectedProductsFactsSkipped = useSelector(deskingSelectors.selectedProductsFactsSkipped);

  const isShowAddToAllOptions = !!selectedProducts?.length && !!Object.keys(builder ?? []).length;
  const isMinMaxRetailCostInvalid = Object.values(selectedProductsConfigurations).some(
    pc => !pc.isMinMaxRetailCostValid
  );

  const handleAddToAllOptions = () => {
    dispatch(deskingActions.addSelectedProductsToMenu());
  };

  const handelUpdateConfig = (productId: string, updateValues: Partial<ProductConfig>) => {
    dispatch(deskingActions.updateProductConfiguration({ productId, data: updateValues }));
  };

  const handelUpdateConfigInMenu = (productId: string, menuName: string, updateValues: Partial<ProductConfig>) => {
    dispatch(deskingActions.updateProductConfigurationInMenu({ menuName, productId, data: updateValues }));
  };

  return (
    <ProposalProductsCards>
      {selectedProducts.map(product => (
        <ProposalProductsCard
          type="simple"
          product={product}
          products={products}
          mode={ProposalProductCardModes.Desking}
          isNeedRunJsonEngine={isNeedRunJsonEngine}
          hideFieldsForRender={hideFieldsForRender}
          key={`simple-${product.proposalProductId}`}
          equipmentCost={currentEquipment?.retailPrice}
          prisingConfigByVendor={prisingConfigByVendor}
          factsToCheck={selectedProductsFactsForCheck[getProductFactHashMapKey(product)]}
          factsSkipped={selectedProductsFactsSkipped[getProductFactHashMapKey(product)]}
          productConfiguration={productConfigurations[product.proposalProductId] ?? {}}
          updateProductConfiguration={updateValues => {
            handelUpdateConfig(product.proposalProductId, updateValues);
          }}
        />
      ))}

      {isShowAddToAllOptions && (
        <Tooltip title="Adds all products & their current configuration to every available menu option in the template">
          <Button variant="outlined" onClick={handleAddToAllOptions} disabled={isMinMaxRetailCostInvalid}>
            Apply Product(s) to All Options
          </Button>
        </Tooltip>
      )}

      {Object.entries(
        selectedMenuOption ? { [selectedMenuOption.name]: selectedMenuOption } : menuOptionsGroupedByName
      ).map(([menuName, option]) => (
        <Fragment key={menuName}>
          {!!option?.products.length && (
            <Box className={classes.container}>
              <TermsStepperManager
                label={menuName}
                styles={{ color: option?.titleColor, backgroundColor: option?.titleBgColor }}
                {...termsManagementProps}
              />

              {option?.products?.map((product: ProposalProduct) => {
                const fullProduct = products.find(p => p.proposalProductId === product.proposalProductId) ?? product;
                return (
                  <ProposalProductsCard
                    type="forEdit"
                    product={fullProduct}
                    products={products}
                    mode={ProposalProductCardModes.Desking}
                    isNeedRunJsonEngine={isNeedRunJsonEngine}
                    hideFieldsForRender={hideFieldsForRender}
                    key={`forEdit-${product.proposalProductId}`}
                    prisingConfigByVendor={prisingConfigByVendor}
                    equipmentCost={currentEquipment?.retailPrice}
                    factsToCheck={selectedProductsFactsForCheck[getProductFactHashMapKey(product)]}
                    factsSkipped={selectedProductsFactsSkipped[getProductFactHashMapKey(product)]}
                    productConfiguration={productConfigurationsInMenu?.[menuName]?.[product.proposalProductId] ?? {}}
                    updateProductConfiguration={updateValues => {
                      handelUpdateConfigInMenu(product.proposalProductId, menuName, updateValues);
                    }}
                  />
                );
              })}
            </Box>
          )}
        </Fragment>
      ))}
    </ProposalProductsCards>
  );
};

const useStyles = makeStyles({
  container: {
    gap: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
});
